/**
 * This file provides utility functions to show snackbar notifications from non-React code.
 * It uses a store-and-forward approach where notifications are stored in memory until
 * the SnackbarProvider is ready to process them.
 */

// Queue to store pending notifications before the provider is ready
let pendingNotifications = [];
let snackbarCallbacks = null;

/**
 * Register the snackbar callbacks from the provider
 * @param {Object} callbacks - Object containing snackbar callback functions
 */
export const registerSnackbarCallbacks = (callbacks) => {
  snackbarCallbacks = callbacks;
  
  // Process any pending notifications
  if (pendingNotifications.length > 0) {
    pendingNotifications.forEach(({ type, message, duration }) => {
      showNotification(type, message, duration);
    });
    pendingNotifications = [];
  }
};

/**
 * Show a notification with the specified type
 * @param {string} type - The type of notification ('success', 'error', 'warning', 'info')
 * @param {string} message - The message to display
 * @param {number} duration - How long to display the notification (in milliseconds)
 */
const showNotification = (type, message, duration) => {
  if (!snackbarCallbacks) {
    // Store notification for later if callbacks aren't registered yet
    pendingNotifications.push({ type, message, duration });
    return;
  }

  switch (type) {
    case 'success':
      snackbarCallbacks.showSuccess(message, duration);
      break;
    case 'error':
      snackbarCallbacks.showError(message, duration);
      break;
    case 'warning':
      snackbarCallbacks.showWarning(message, duration);
      break;
    case 'info':
      snackbarCallbacks.showInfo(message, duration);
      break;
    default:
      snackbarCallbacks.showSnackbar(message, type, duration);
  }
};

/**
 * Show a success notification
 * @param {string} message - The message to display
 * @param {number} duration - How long to display the notification (in milliseconds)
 */
export const showSuccess = (message, duration) => {
  showNotification('success', message, duration);
};

/**
 * Show an error notification
 * @param {string} message - The message to display
 * @param {number} duration - How long to display the notification (in milliseconds)
 */
export const showError = (message, duration) => {
  showNotification('error', message, duration);
};

/**
 * Show a warning notification
 * @param {string} message - The message to display
 * @param {number} duration - How long to display the notification (in milliseconds)
 */
export const showWarning = (message, duration) => {
  showNotification('warning', message, duration);
};

/**
 * Show an info notification
 * @param {string} message - The message to display
 * @param {number} duration - How long to display the notification (in milliseconds)
 */
export const showInfo = (message, duration) => {
  showNotification('info', message, duration);
};

/**
 * Show a generic notification
 * @param {string} message - The message to display
 * @param {string} type - The type of notification ('success', 'error', 'warning', 'info')
 * @param {number} duration - How long to display the notification (in milliseconds)
 */
export const showSnackbar = (message, type = 'info', duration) => {
  showNotification(type, message, duration);
}; 